/**
 * Support menu — top right of the admin portal.
 *
 * Links to the documentation (docs.eurion.se) and the support portal (support.eurion.se),
 * and shows the facts a support case needs: running version, update channel, domain and,
 * when the license API exists on this server, the license. "Open a ticket" passes those
 * facts to the support portal as query parameters, so the customer sees and can edit them
 * before anything is sent. Nothing is sent server-to-server.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { LifeBuoy, BookOpen, Download, Wrench, ScrollText, MessageSquarePlus, Copy, Check, ChevronDown } from 'lucide-react';

export const DOCS_URL = (import.meta.env.VITE_DOCS_URL as string | undefined) ?? 'https://docs.eurion.se';
export const SUPPORT_URL = (import.meta.env.VITE_SUPPORT_URL as string | undefined) ?? 'https://support.eurion.se';

export interface ServerInfo {
  version: string | null;
  channel: string | null;
  domain: string;
  license: { id: string; state: string; users: string } | null;
}

/** admin.example.se → example.se; a single-hostname install keeps its hostname. */
export function domainFromHost(host: string): string {
  return host.startsWith('admin.') ? host.slice('admin.'.length) : host;
}

/** Release notes page for a version: 1.6.2 → /versioner/1.6.2; 1.7.0-test1 → /versioner/1.7.0. */
export function releaseNotesUrl(version: string | null): string {
  const base = version?.match(/^\d+\.\d+\.\d+/)?.[0];
  return base ? `${DOCS_URL}/versioner/${base}` : `${DOCS_URL}/versioner/`;
}

export function ticketUrl(info: ServerInfo): string {
  const q = new URLSearchParams();
  if (info.version) q.set('version', info.version);
  if (info.channel) q.set('channel', info.channel);
  q.set('domain', info.domain);
  if (info.license) q.set('license', info.license.id);
  return `${SUPPORT_URL}/new?${q.toString()}`;
}

export function infoText(info: ServerInfo): string {
  return [
    `Eurion version: ${info.version ?? 'unknown'}`,
    `Update channel: ${info.channel ?? 'unknown'}`,
    `Domain: ${info.domain}`,
    `License: ${info.license ? `${info.license.id} (${info.license.state}, ${info.license.users} users)` : 'none'}`,
  ].join('\n');
}

async function getData<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const body = await res.json() as { success?: boolean; data?: T };
    return body.success === false ? null : body.data ?? null;
  } catch {
    return null;
  }
}

interface UpdatesStatus { current_version: string | null; channel_url: string | null }
interface LicenseView {
  status: { state: string; activeUsers: number; maxActiveUsers: number };
  license: { license_id: string } | null;
}

export async function loadServerInfo(host = window.location.hostname): Promise<ServerInfo> {
  // The license API only exists once licensing ships; a 404 just means "no license info".
  const [updates, lic] = await Promise.all([
    getData<UpdatesStatus>('/v1/admin/system/updates'),
    getData<LicenseView>('/v1/admin/license'),
  ]);
  return {
    version: updates?.current_version ?? null,
    channel: updates?.channel_url ?? null,
    domain: domainFromHost(host),
    license: lic?.license
      ? { id: lic.license.license_id, state: lic.status.state, users: `${lic.status.activeUsers}/${lic.status.maxActiveUsers}` }
      : null,
  };
}

export default function SupportMenu() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<ServerInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && !info) void loadServerInfo().then(setInfo);
  }, [open, info]);

  const close = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); close(true); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const copy = async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(infoText(info));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked: the text is visible to copy by hand */ }
  };

  const version = info?.version ?? null;
  const links = [
    { label: 'Documentation', href: DOCS_URL, icon: BookOpen },
    { label: 'Install & update guide', href: `${DOCS_URL}/uppdatering/`, icon: Download },
    { label: 'Troubleshooting', href: `${DOCS_URL}/felsokning`, icon: Wrench },
    { label: version ? `Release notes for ${version}` : 'Release notes', href: releaseNotesUrl(version), icon: ScrollText },
  ];

  return (
    <div ref={rootRef} className="relative" onKeyDown={open ? onKeyDown : undefined}>
      <button ref={buttonRef} type="button" aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-nord-4 transition hover:bg-nord-2 hover:text-nord-6">
        <LifeBuoy className="h-4 w-4" /> Support <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div ref={menuRef} role="menu" aria-label="Support"
          className="absolute right-0 z-50 mt-1 w-80 rounded-xl border border-nord-2 bg-nord-1 py-1 shadow-xl">
          {links.map(({ label, href, icon: Icon }) => (
            <a key={label} role="menuitem" href={href} target="_blank" rel="noopener noreferrer"
              onClick={() => close(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-nord-4 outline-none hover:bg-nord-2 focus:bg-nord-2">
              <Icon className="h-4 w-4 flex-shrink-0" /> {label}
            </a>
          ))}
          <a role="menuitem" href={info ? ticketUrl(info) : `${SUPPORT_URL}/new`} target="_blank" rel="noopener noreferrer"
            onClick={() => close(false)}
            className="flex items-center gap-2.5 px-4 py-2 text-sm font-medium text-nord-8 outline-none hover:bg-nord-2 focus:bg-nord-2">
            <MessageSquarePlus className="h-4 w-4 flex-shrink-0" /> Contact support / open a ticket
          </a>
          <div className="mx-3 mt-1 mb-2 rounded-lg border border-nord-2 bg-nord-0 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-nord-3">Server info for support</span>
              <button type="button" role="menuitem" onClick={copy} disabled={!info}
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-nord-4 outline-none hover:bg-nord-2 focus:bg-nord-2 disabled:opacity-50">
                {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
              </button>
            </div>
            {info ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                <dt className="text-nord-4">Version</dt><dd className="text-nord-5">{info.version ?? 'unknown'}</dd>
                <dt className="text-nord-4">Channel</dt><dd className="truncate text-nord-5" title={info.channel ?? ''}>{info.channel ?? 'unknown'}</dd>
                <dt className="text-nord-4">Domain</dt><dd className="text-nord-5">{info.domain}</dd>
                <dt className="text-nord-4">License</dt>
                <dd className="text-nord-5">{info.license ? `${info.license.id} · ${info.license.state} · ${info.license.users} users` : '—'}</dd>
              </dl>
            ) : (
              <p className="text-xs text-nord-3">Loading…</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
